// AI TEX 아이콘 — 모든 아이콘의 단일 진입점. 원본 SVG 는 src/assets/graphics/icons/<name>.svg 와 동일하다.
// 규격: 24 그리드 · 1.75px 스트로크 · round cap/join · currentColor (docs/design-concept.md §4).
// 쓰임:  <Icon name="search" size={16} title="검색" />   → title 이 있으면 role="img", 없으면 aria-hidden.
// 갱신은 web-graphic-artist 가 한다 — 화면 쪽에서 이 파일의 ICONS 를 직접 고치지 않는다.
// 'loader' 는 원호만 그린다 — 회전은 화면 CSS(@keyframes, prefers-reduced-motion 이면 정지)가 맡는다.
// 'x-circle' 은 'error' 와 같은 기하다 — 상태 표시는 error, 거부·반려 같은 행동 버튼은 x-circle 로 이름을 나눈다.
// 'grid' · 'list' 는 한 쌍(보기 전환)이다 — 왼쪽 칸(7×7, rx 1.5)을 공유하고 오른쪽만 칸 ↔ 가로줄로 바뀐다.
//   'dashboard'(칸 크기가 다른 관리 대시보드)와 달리 grid 는 같은 크기 칸 · 좁은 간격(3.5)이다. 18px 에서 선이 픽셀에 맞도록 좌표를 골랐다.
// 같은 기하를 공유하는 계열 (2026-10-17) — 한쪽을 고치면 다른 쪽도 같이 고친다. 그래픽 시트가 자동으로 대조한다.
//   'zoom-in' = 'search' 돋보기 그대로 + 가운데 플러스.  'bookmark-check' = 'bookmark' 그대로 + 'shield-check' 와 같은 체크.
//   'expand' ↔ 'shrink' = 네 모서리의 같은 5×5 칸에서 expand 는 바깥 두 변, shrink 는 안쪽 두 변을 긋는다(끝점이 같다).
//   'bookmark' 의 담김(선택) 상태는 채우지 않는다 — 화면이 color: var(--accent) 로 칠하고 아이콘을 bookmark-check 로 바꾼다.
//   'rotate'(사각형 + 시계방향 사분원) 은 'refresh'(거의 닫힌 원) · 'history'(반시계) 와 모양으로 구분된다.
// 3단계(R3) 묶음 (2026-10-18) — 좌표는 16px 에서 픽셀 중심(0.75 + 1.5n)에 놓이도록 골랐다.
//   'bookmark-search' = 'bookmark' 왼쪽 반(오른쪽 아래를 비움) + 'image-search' 와 같은 모서리 돋보기(마지막 두 요소가 같다).
//     담기(bookmark · bookmark-check)는 책갈피 윤곽이 온전하고, 이 검색 저장은 윤곽이 돋보기로 끊긴다 — 실루엣으로 구분.
//   'trend-down' = 'trend-up' 위아래 거울(y → 24 − y).  'select-all' 의 체크 = 'check-circle' 의 체크(같은 자리).
//   'sliders'(세로 막대 3 + 가로 손잡이) ↔ 'settings'(가로 2단 + 동그란 손잡이) ↔ 'filter'(깔때기) 는 방향 · 손잡이 모양으로 구분.
//   'undo'(왼쪽 화살촉 + U 자로 꺾인 대) 는 'refresh' · 'history'(원) · 'arrow-left'(곧은 대) 와 다르다.
//   'table-export'(표 + 오른쪽 화살표) 는 'download'(받침 + 아래 화살표) 와 방향 · 그릇이 다르다.
// R4 묶음 (2026-10-18, 관리자 원단 편집 · 휴지통 · 변경 이력 — 요청서 docs/design-reviews/20261018-14-assets-request.md)
//   'trash-restore' = 'trash' 의 뚜껑 · 손잡이 · 몸통(앞 세 요소) 그대로 + 안쪽 세로줄 대신 위 화살표. 시트가 대조한다.
//     'undo'(U 자 화살표 — 「모두 되돌리기」) 와는 그릇(휴지통)이 있느냐로, 'trash'(삭제) 와는 안쪽 무늬(줄 둘 ↔ ↑)로 갈린다.
//   'edit-history' = 아래 오른쪽이 트인 시계(r7.5) + 바늘 + 그 자리의 작은 연필('edit' 과 같은 촉 각도).
//     'history'(반시계 화살표 원 — 관리 메뉴 「감사 기록」) · 'clock'(닫힌 원) 과 윤곽이 다르다.
import type { CSSProperties, SVGProps } from 'react'

const ICONS = {
  'search':
    '<circle cx="11" cy="11" r="6.5"/><path d="M15.6 15.6 20.5 20.5"/>',
  'image-search':
    '<path d="M12.5 20H5.5A1.5 1.5 0 0 1 4 18.5v-13A1.5 1.5 0 0 1 5.5 4h12A1.5 1.5 0 0 1 19 5.5V12"/><path d="M4 16l4-4 3 3 2.5-2.5"/><circle cx="17.5" cy="17.5" r="3"/><path d="M19.7 19.7 21.5 21.5"/>',
  'camera':
    '<path d="M3 9a2 2 0 0 1 2-2h2.5l1.5-2.25h6L16.5 7H19a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><circle cx="12" cy="13" r="3.5"/>',
  'filter':
    '<path d="M3 5h18l-7 8v6l-4 2v-8z"/>',
  'close':
    '<path d="M6 6l12 12M18 6 6 18"/>',
  'chevron-left':
    '<path d="M15 5l-7 7 7 7"/>',
  'chevron-right':
    '<path d="M9 5l7 7-7 7"/>',
  'chevron-down':
    '<path d="M5 9l7 7 7-7"/>',
  'arrow-left':
    '<path d="M20 12H4M10 6l-6 6 6 6"/>',
  'home':
    '<path d="M4 11l8-7 8 7v8a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19z"/><path d="M9.5 20.5V15h5v5.5"/>',
  'user':
    '<circle cx="12" cy="8" r="4"/><path d="M4.5 20.5a7.5 7.5 0 0 1 15 0"/>',
  'users':
    '<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0"/><path d="M15.5 4.7a3.5 3.5 0 0 1 0 6.6"/><path d="M17.5 13.7a6.5 6.5 0 0 1 4 6.3"/>',
  'lock':
    '<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7.5a4 4 0 0 1 8 0V11"/><path d="M12 15v2"/>',
  'unlock':
    '<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7.5a4 4 0 0 1 7.8-1.2"/><path d="M12 15v2"/>',
  'check':
    '<path d="M5 12.5l4.5 4.5L19 7"/>',
  'warning':
    '<path d="M12 4.5 3 19.5h18z"/><path d="M12 10v4M12 17.25h.01"/>',
  'info':
    '<circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/>',
  'error':
    '<circle cx="12" cy="12" r="9"/><path d="M9.5 9.5l5 5M14.5 9.5l-5 5"/>',
  'logout':
    '<path d="M10 4H5.5A1.5 1.5 0 0 0 4 5.5v13A1.5 1.5 0 0 0 5.5 20H10"/><path d="M20 12H9M16 8l4 4-4 4"/>',
  'settings':
    '<path d="M4 8h7.25M17.75 8H20M4 16h2.25M12.75 16H20"/><circle cx="14.5" cy="8" r="2.5"/><circle cx="9.5" cy="16" r="2.5"/>',
  'refresh':
    '<path d="M20 12a8 8 0 1 1-2.34-5.66L20 8.5"/><path d="M20 3.5v5h-5"/>',
  'copy':
    '<rect x="9" y="9" width="11" height="11" rx="1.5"/><path d="M15 9V5.5A1.5 1.5 0 0 0 13.5 4h-8A1.5 1.5 0 0 0 4 5.5v8A1.5 1.5 0 0 0 5.5 15H9"/>',
  'eye':
    '<path d="M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z"/><circle cx="12" cy="12" r="3"/>',
  'eye-off':
    '<path d="M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z"/><circle cx="12" cy="12" r="3"/><path d="M4 4l16 16"/>',
  'swatch':
    '<path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v9L14.5 20h-9A1.5 1.5 0 0 1 4 18.5z"/><path d="M20 14.5h-4a1.5 1.5 0 0 0-1.5 1.5v4"/><path d="M8 9h8M8 13h4"/>',
  'ruler':
    '<rect x="2.5" y="8" width="19" height="8" rx="1.5"/><path d="M6.5 16v-3M10.5 16v-4M14.5 16v-3M18.5 16v-4"/>',
  'fabric':
    '<path d="M4 6c3-2.5 5-2.5 8 0s5 2.5 8 0v12c-3 2.5-5 2.5-8 0s-5-2.5-8 0z"/><path d="M4 12c3-2.5 5-2.5 8 0s5 2.5 8 0"/>',
  'thread':
    '<path d="M6 4h12M6 20h12M8 4v16M16 4v16"/><path d="M8 10l8-2.5M8 15l8-2.5"/><path d="M16 17c2.5 0 4 1.5 4.5 3.5"/>',
  'palette':
    '<path d="M12 3.5a8.5 8.5 0 1 0 0 17c1.4 0 2-1 1.6-2.2-.4-1.2 0-2.3 1.4-2.3h1.5a4 4 0 0 0 4-4A8.5 8.5 0 0 0 12 3.5z"/><circle cx="8.5" cy="12.5" r="1.1"/><circle cx="10.5" cy="8.5" r="1.1"/><circle cx="14.5" cy="8" r="1.1"/>',
  'pattern-repeat':
    '<path d="M7.5 4.75l2.75 2.75L7.5 10.25 4.75 7.5zM16.5 4.75l2.75 2.75-2.75 2.75-2.75-2.75zM7.5 13.75l2.75 2.75L7.5 19.25l-2.75-2.75zM16.5 13.75l2.75 2.75-2.75 2.75-2.75-2.75z"/>',
  'upload':
    '<path d="M4 16v3a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 20 19v-3"/><path d="M12 15V4M7.5 8.5 12 4l4.5 4.5"/>',
  'download':
    '<path d="M4 16v3a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 20 19v-3"/><path d="M12 4v11M7.5 10.5 12 15l4.5-4.5"/>',
  'external':
    '<path d="M14 4h6v6M20 4 11 13"/><path d="M18 13.5v5A1.5 1.5 0 0 1 16.5 20h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6h5"/>',
  'menu':
    '<path d="M4 7h16M4 12h16M4 17h16"/>',
  'plus':
    '<path d="M12 5v14M5 12h14"/>',
  'minus':
    '<path d="M5 12h14"/>',
  'star':
    '<path d="M12 3.5 14.59 8.94 20.56 9.72 16.18 13.86 17.29 19.78 12 16.9 6.71 19.78 7.82 13.86 3.44 9.72 9.41 8.94z"/>',
  'clock':
    '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/>',
  'shield':
    '<path d="M12 3.5 4.5 6.5v5c0 4.5 3 8 7.5 9.5 4.5-1.5 7.5-5 7.5-9.5v-5z"/>',
  'pending':
    '<path d="M7 4h10M7 20h10"/><path d="M8.5 4v2.5c0 3.5 3.5 4 3.5 5.5s-3.5 2-3.5 5.5V20M15.5 4v2.5c0 3.5-3.5 4-3.5 5.5s3.5 2 3.5 5.5V20"/>',
  'login':
    '<path d="M14 4h4.5A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5H14"/><path d="M4 12h11M11 8l4 4-4 4"/>',
  'user-plus':
    '<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0"/><path d="M18.5 8v6M15.5 11h6"/>',
  'user-x':
    '<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0"/><path d="M16 8.5l5 5M21 8.5l-5 5"/>',
  'mail':
    '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3.5 6.5 12 13l8.5-6.5"/>',
  'key':
    '<circle cx="8" cy="16" r="4.5"/><path d="M11.18 12.82 20 4M17.5 6.5 20 9M15 9l2 2"/>',
  'building':
    '<path d="M4 20.5V5.5A1.5 1.5 0 0 1 5.5 4h7A1.5 1.5 0 0 1 14 5.5v15"/><path d="M14 10h4.5a1.5 1.5 0 0 1 1.5 1.5v9"/><path d="M2.5 20.5h19M7.5 8h3M7.5 12h3M7.5 16h3"/>',
  'phone':
    '<rect x="6" y="3" width="12" height="18" rx="2.5"/><path d="M10.5 17.5h3"/>',
  'message':
    '<path d="M5.5 4h13A1.5 1.5 0 0 1 20 5.5v10a1.5 1.5 0 0 1-1.5 1.5H11l-4 3.5V17H5.5A1.5 1.5 0 0 1 4 15.5v-10A1.5 1.5 0 0 1 5.5 4z"/><path d="M8 9h8M8 12.5h5"/>',
  'sun':
    '<circle cx="12" cy="12" r="4"/><path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.64 5.64 7.4 7.4M16.6 16.6l1.76 1.76M5.64 18.36 7.4 16.6M16.6 7.4l1.76-1.76"/>',
  'moon':
    '<path d="M11.5 4a8.5 8.5 0 1 0 8.5 8.5 6.5 6.5 0 0 1-8.5-8.5z"/>',
  'monitor':
    '<rect x="3" y="4" width="18" height="12" rx="1.5"/><path d="M12 16v4M8 20h8"/>',
  'dashboard':
    '<rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/>',
  'history':
    '<path d="M4 12a8 8 0 1 0 2.34-5.66L4 8.5"/><path d="M4 3.5v5h5"/><path d="M12 7.5V12l3 2"/>',
  'save':
    '<path d="M5.5 4H16l4 4v10.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5v-13A1.5 1.5 0 0 1 5.5 4z"/><path d="M8 20v-6h8v6M8 4v4h6"/>',
  'edit':
    '<path d="M16.5 4.5a2.48 2.48 0 0 1 3.5 3.5L8.5 19.5l-4.75 1.25L5 16z"/><path d="M14.5 6.5 18 10"/>',
  'trash':
    '<path d="M4 7h16"/><path d="M9.5 7V5.5A1.5 1.5 0 0 1 11 4h2a1.5 1.5 0 0 1 1.5 1.5V7"/><path d="M6 7l.85 12.1a1.5 1.5 0 0 0 1.5 1.4h7.3a1.5 1.5 0 0 0 1.5-1.4L18 7"/><path d="M10 11v5.5M14 11v5.5"/>',
  'send':
    '<path d="M20.5 3.5 3.5 10l7 3.5 3.5 7z"/><path d="M20.5 3.5l-10 10"/>',
  'sparkle':
    '<path d="M10.5 5.5Q11.5 12.5 18.5 13.5 11.5 14.5 10.5 21.5 9.5 14.5 2.5 13.5 9.5 12.5 10.5 5.5z"/><path d="M18.5 3v4M16.5 5h4"/>',
  'shield-check':
    '<path d="M12 3.5 4.5 6.5v5c0 4.5 3 8 7.5 9.5 4.5-1.5 7.5-5 7.5-9.5v-5z"/><path d="M8.75 12l2.25 2.25 4.25-4.5"/>',
  'check-circle':
    '<circle cx="12" cy="12" r="9"/><path d="M8 12.25l2.75 2.75L16 9.75"/>',
  'x-circle':
    '<circle cx="12" cy="12" r="9"/><path d="M9.5 9.5l5 5M14.5 9.5l-5 5"/>',
  'loader':
    '<path d="M12 3.5a8.5 8.5 0 1 1-8.5 8.5"/>',
  'grid':
    '<rect x="3.25" y="3.25" width="7" height="7" rx="1.5"/><rect x="13.75" y="3.25" width="7" height="7" rx="1.5"/><rect x="3.25" y="13.75" width="7" height="7" rx="1.5"/><rect x="13.75" y="13.75" width="7" height="7" rx="1.5"/>',
  'list':
    '<rect x="3.25" y="3.25" width="7" height="7" rx="1.5"/><rect x="3.25" y="13.75" width="7" height="7" rx="1.5"/><path d="M13.75 4.75h7M13.75 8.75h7M13.75 15.25h7M13.75 19.25h7"/>',
  // ── U1 · U2 · U5 (웹 UI 개선 계획 2단계: 담기 · 비교 · 사진 자르기 · 확대 보기) — 2026-10-17
  'bookmark':
    '<path d="M5.5 5A1.5 1.5 0 0 1 7 3.5h10A1.5 1.5 0 0 1 18.5 5v15.5L12 16.5l-6.5 4z"/>',
  'bookmark-check':
    '<path d="M5.5 5A1.5 1.5 0 0 1 7 3.5h10A1.5 1.5 0 0 1 18.5 5v15.5L12 16.5l-6.5 4z"/><path d="M8.75 9.75l2.25 2.25 4.25-4.5"/>',
  'compare':
    '<rect x="3" y="5" width="18" height="14" rx="1.5"/><path d="M12 3v18M8.75 9.5 6.25 12l2.5 2.5M15.25 9.5l2.5 2.5-2.5 2.5"/>',
  'folder-stack':
    '<path d="M6.5 5.5A1.5 1.5 0 0 1 8 4h3.25l2 2h6.25A1.5 1.5 0 0 1 21 7.5V15a1.5 1.5 0 0 1-1.5 1.5H8A1.5 1.5 0 0 1 6.5 15z"/><path d="M3 7.5v11A1.5 1.5 0 0 0 4.5 20h13"/>',
  'crop':
    '<path d="M7 3v14h14M3 7h14v14"/>',
  'rotate':
    '<rect x="3" y="10.25" width="9" height="9" rx="1.5"/><path d="M3.5 5.58A10 10 0 0 1 17.5 14.75"/><path d="M14.5 11.75l3 3 3-3"/>',
  'zoom-in':
    '<circle cx="11" cy="11" r="6.5"/><path d="M15.6 15.6 20.5 20.5"/><path d="M11 8v6M8 11h6"/>',
  'expand':
    '<path d="M3.5 8.5V5A1.5 1.5 0 0 1 5 3.5h3.5M15.5 3.5H19A1.5 1.5 0 0 1 20.5 5v3.5M20.5 15.5V19a1.5 1.5 0 0 1-1.5 1.5h-3.5M8.5 20.5H5A1.5 1.5 0 0 1 3.5 19v-3.5"/>',
  'shrink':
    '<path d="M8.5 3.5V7A1.5 1.5 0 0 1 7 8.5H3.5M20.5 8.5H17A1.5 1.5 0 0 1 15.5 7V3.5M15.5 20.5V17a1.5 1.5 0 0 1 1.5-1.5h3.5M3.5 15.5H7A1.5 1.5 0 0 1 8.5 17v3.5"/>',
  // ── R3 (웹 UI 개선 계획 3단계: 검수 단축키 · 저장한 검색 · 일괄 처리 · 대시보드 · 필터 시트 · 배색 · 빈 상태) — 2026-10-18
  'keyboard':
    '<rect x="2.25" y="5.25" width="19.5" height="13.5" rx="2"/><path d="M5.25 9.75h.01M9.75 9.75h.01M14.25 9.75h.01M18.75 9.75h.01M5.25 14.25h.01M9.75 14.25h4.5M18.75 14.25h.01"/>',
  'bell':
    '<path d="M6.75 9a5.25 5.25 0 0 1 10.5 0v5.25c0 1.25.75 2.25 2.25 3H4.5c1.5-.75 2.25-1.75 2.25-3z"/><path d="M10.5 20.25h3"/>',
  'bookmark-search':
    '<path d="M18.5 12V5A1.5 1.5 0 0 0 17 3.5H7A1.5 1.5 0 0 0 5.5 5v15.5L12 16.5"/><circle cx="17.5" cy="17.5" r="3"/><path d="M19.7 19.7 21.5 21.5"/>',
  'undo':
    '<path d="M8.25 3.75 3.75 8.25l4.5 4.5"/><path d="M3.75 8.25h10.5a6 6 0 0 1 0 12h-4.5"/>',
  'table-export':
    '<path d="M11.25 20.25h-6a1.5 1.5 0 0 1-1.5-1.5V5.25a1.5 1.5 0 0 1 1.5-1.5h12a1.5 1.5 0 0 1 1.5 1.5v6"/><path d="M3.75 8.25h15M3.75 14.25h7.5M9.75 8.25v12"/><path d="M14.25 17.25h7.5M18.75 14.25l3 3-3 3"/>',
  'calendar':
    '<rect x="3.75" y="5.25" width="16.5" height="15" rx="2"/><path d="M3.75 9.75h16.5M8.25 3.75v3M15.75 3.75v3"/>',
  'trend-up':
    '<path d="M3.75 17.25l6-6 3 3 7.5-7.5"/><path d="M15.75 6.75h4.5v4.5"/>',
  'trend-down':
    '<path d="M3.75 6.75l6 6 3-3 7.5 7.5"/><path d="M15.75 17.25h4.5v-4.5"/>',
  'inbox':
    '<path d="M6.75 3.75h10.5l3 9v6a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-6z"/><path d="M3.75 12.75h4.5l1.5 3h4.5l1.5-3h4.5"/>',
  'sliders':
    '<path d="M5.25 20.25v-6M5.25 10.5V3.75M12 20.25V9.75M12 6V3.75M18.75 20.25v-4.5M18.75 12V3.75M3 14.25h4.5M9.75 9.75h4.5M16.5 15.75h4.5"/>',
  'layers':
    '<path d="M12 4 21 9.25l-9 5.25-9-5.25z"/><path d="M3 14.75 12 20l9-5.25"/>',
  'select-all':
    '<rect x="3.75" y="3.75" width="16.5" height="16.5" rx="2.5"/><path d="M8 12.25l2.75 2.75L16 9.75"/>',
  // ── R4 (관리자 원단 편집 · 휴지통 · 변경 이력, 요청서 20261018-14) — 2026-10-18
  'trash-restore':
    '<path d="M4 7h16"/><path d="M9.5 7V5.5A1.5 1.5 0 0 1 11 4h2a1.5 1.5 0 0 1 1.5 1.5V7"/><path d="M6 7l.85 12.1a1.5 1.5 0 0 0 1.5 1.4h7.3a1.5 1.5 0 0 0 1.5-1.4L18 7"/><path d="M12 17.25V10M9.5 12.5 12 10l2.5 2.5"/>',
  'edit-history':
    '<path d="M11.37 17.07A7.5 7.5 0 1 1 17.1 11.25"/><path d="M9.75 5.25v4.5l3 1.5"/><path d="M19.44 12.94a1.5 1.5 0 0 1 2.12 2.12L15.9 20.72 13 21.5l.78-2.9z"/>',
} as const

export type IconName = keyof typeof ICONS
export const ICON_NAMES = Object.keys(ICONS) as IconName[]

const SPRITE_ID = 'aitex-icon-sprite'
const SPRITE_ATTRS = 'fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"'

function spriteMarkup(): string {
  return ICON_NAMES.map(n => `<symbol id="i-${n}" viewBox="0 0 24 24" ${SPRITE_ATTRS}>${ICONS[n]}</symbol>`).join('')
}

/* 스프라이트는 모듈이 처음 로드될 때 한 번 <body> 맨 앞에 붙는다.
   SSR 등 document 가 없는 환경에서는 <IconSprite/> 를 직접 렌더한다. */
if (typeof document !== 'undefined' && !document.getElementById(SPRITE_ID)) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.id = SPRITE_ID
  svg.setAttribute('aria-hidden', 'true')
  svg.setAttribute('focusable', 'false')
  svg.setAttribute('style', 'position:absolute;width:0;height:0;overflow:hidden')
  svg.innerHTML = spriteMarkup()
  document.body.prepend(svg)
}

export function IconSprite() {
  return (
    <svg
      id={SPRITE_ID}
      aria-hidden="true"
      focusable="false"
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
      dangerouslySetInnerHTML={{ __html: spriteMarkup() }}
    />
  )
}

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name' | 'width' | 'height'> {
  /** 아이콘 이름 — 없는 이름은 컴파일 오류. */
  name: IconName
  /** 한 변 크기. 숫자는 px, 기본 1em(글자 크기를 따른다). */
  size?: number | string
  /** 혼자 뜻을 전하는 아이콘이면 적는다 → role="img" + <title>. 없으면 장식(aria-hidden). */
  title?: string
}

export function Icon({ name, size = '1em', title, style, ...rest }: IconProps) {
  const a11y = title ? { role: 'img' as const } : { 'aria-hidden': true as const }
  const mergedStyle: CSSProperties = { flexShrink: 0, verticalAlign: '-0.125em', ...style }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      style={mergedStyle}
      {...a11y}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <use href={`#i-${name}`} />
    </svg>
  )
}

export default Icon
